import React, { useState, useRef, useEffect } from "react";
import { Column, FleetrifyCustomTable } from "../CustomTable/customTable";
import { Search, MoreVertical } from "lucide-react";

export interface Staff {
  company_name: string;
  branch_id?: string;
  id: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  email?: string;
  role?: string;
  branch?: string;
  status?: string;
  dateCreated?: string;
  dateOfResumption?: string;
  phone_number?: string;
  profileImage?: string;
  company?: string;
  role_id?: string;
  company_id?: string;
  resumption_date?: string;
  gender?: string;
  permissions?: string[];
}

interface StaffTableProps {
  onRowClick: (staff: Staff) => void;
  onActionClick?: (
    action: "update" | "updatestaff" | "resend" | "delete" | "reactivate" | "deactivate",
    staff: Staff,
    updateRow?: (updatedStaff: Staff) => void
  ) => void;
  tableData: Staff[];
  pageSize?: number;
  totalPage?: number;
  total?: number;
  currentPage?: number;
  setCurrentPage?: (page: number) => void;
  showActions?: boolean;
  view: string;
}

const StaffTable: React.FC<StaffTableProps> = ({
  onRowClick,
  onActionClick,
  tableData,
  pageSize = 15,
  totalPage = 1,
  total,
  currentPage = 1,
  setCurrentPage,
  showActions = false,
  view,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [openActionMenuId, setOpenActionMenuId] = useState<string | null>(null);
  const [localData, setLocalData] = useState<Staff[]>(tableData);

  const actionMenuRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Update localData when tableData changes from parent
  useEffect(() => {
    setLocalData(tableData);
  }, [tableData]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedOutsideAllMenus = Object.values(actionMenuRefs.current).every(
        (ref) => ref && !ref.contains(event.target as Node)
      );
      if (clickedOutsideAllMenus) setOpenActionMenuId(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredData = localData.filter((row) =>
    (row.name ?? "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns: Column[] = [
    { label: "Name", key: "name", sortable: true },
    { label: "Email", key: "email", sortable: true },
    { label: "Role", key: "role", sortable: true },
    {
      label: "Branch",
      key: "branch",
      sortable: true,
      render: (_: any, row: Staff) => {
        const branchValue = row.branch?.trim();
        return <span>{branchValue && branchValue !== "-" ? branchValue : "Not Specified"}</span>;
      },
    },
    {
      label: "Status",
      key: "status",
      sortable: true,
      render: (_: any, row: Staff) => {
        let className = "";
        switch (row.status) {
          case "Active":
            className = "border-green-500 text-green-600 bg-green-50";
            break;
          case "In-active":
            className = "border-gray-500 text-gray-700 bg-gray-200";
            break;
          case "Pending":
            className = "border-amber-500 text-amber-600 bg-amber-50";
            break;
          case "Declined":
            className = "border-red-500 text-red-600 bg-red-50";
            break;
          default:
            className = "border-gray-300 text-gray-600 bg-gray-100";
        }
        return (
          <span className={`px-3 py-1 rounded-full border text-sm font-medium ${className}`}>
            {row.status}
          </span>
        );
      },
    },
    { label: "Date Created", key: "dateCreated", sortable: true },
  ];

  // actions column
  if ((view === "overview" || showActions) && onActionClick) {
    columns.push({
      label: "Actions",
      key: "actions",
      render: (_: any, row: Staff) => (
        <div
          className="relative"
          ref={(el) => {
            actionMenuRefs.current[row.id] = el;
          }}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              setOpenActionMenuId(openActionMenuId === row.id ? null : row.id);
            }}
            className="p-1 hover:bg-gray-100 rounded transition"
          >
            <MoreVertical size={18} />
          </button>

          {openActionMenuId === row.id && (
            <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
              <ul className="py-1 text-sm">
                {view === "overview" && (
                  <>
                    <li
                      onClick={(e) => {
                        e.stopPropagation();
                        onActionClick("updatestaff", row, (updated) => {
                          setLocalData((prev) =>
                            prev.map((s) => (s.id === updated.id ? updated : s))
                          );
                        });
                        setOpenActionMenuId(null);
                      }}
                      className="px-4 py-2 hover:bg-blue-50 cursor-pointer text-gray-600"
                    >
                      Update
                    </li>
                    {row.status === "Active" ? (
                      <li
                        onClick={(e) => {
                          e.stopPropagation();
                          onActionClick("deactivate", row, (updated) => {
                            setLocalData((prev) =>
                              prev.map((s) => (s.id === updated.id ? updated : s))
                            );
                          });
                          setOpenActionMenuId(null);
                        }}
                        className="px-4 py-2 hover:bg-orange-50 cursor-pointer text-orange-600"
                      >
                        Deactivate
                      </li>
                    ) : (
                      <li
                        onClick={(e) => {
                          e.stopPropagation();
                          onActionClick("reactivate", row, (updated) => {
                            setLocalData((prev) =>
                              prev.map((s) => (s.id === updated.id ? updated : s))
                            );
                          });
                          setOpenActionMenuId(null);
                        }}
                        className="px-4 py-2 hover:bg-green-50 cursor-pointer text-green-600"
                      >
                        Reactivate
                      </li>
                    )}
                    <li
                      onClick={(e) => {
                        e.stopPropagation();
                        onActionClick("delete", row, (updated) => {
                          setLocalData((prev) =>
                            prev.filter((s) => s.id !== updated.id)
                          );
                        });
                        setOpenActionMenuId(null);
                      }}
                      className="px-4 py-2 hover:bg-red-50 cursor-pointer text-red-600"
                    >
                      Delete
                    </li>
                  </>
                )}

                {view === "invitation" &&
                  ["update", "resend", "delete"].map((action) => {
                    let textColor = "";
                    switch (action) {
                      case "update":
                        textColor = "text-gray-600 hover:bg-blue-50";
                        break;
                      case "resend":
                        textColor = "text-green-600 hover:bg-green-50";
                        break;
                      case "delete":
                        textColor = "text-red-600 hover:bg-red-50";
                        break;
                      default:
                        textColor = "text-gray-700 hover:bg-gray-100";
                    }

                    return (
                      <li
                        key={action}
                        onClick={(e) => {
                          e.stopPropagation();
                          onActionClick(action as any, row, (updated) => {
                            setLocalData((prev) =>
                              prev.map((s) => (s.id === updated.id ? updated : s))
                            );
                          });
                          setOpenActionMenuId(null);
                        }}
                        className={`px-4 py-2 cursor-pointer capitalize ${textColor}`}
                      >
                        {action}
                      </li>
                    );
                  })}
              </ul>
            </div>
          )}
        </div>
      ),
    });
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="relative w-full sm:max-w-sm">
          <input
            type="text"
            placeholder="Search staff by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-3 py-2 border rounded-full w-full focus:outline-none focus:ring-2 focus:ring-[#B9C3DB]"
          />
          <Search
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            size={18}
          />
        </div>
      </div>

      <FleetrifyCustomTable
        onRowClick={onRowClick}
        data={filteredData}
        columns={columns}
        checkbox={false}
        tooltipLeft
        hideFooter={false}
        currentPage={currentPage}
        lastPage={totalPage}
        total={total}
        setCurrentPage={setCurrentPage}
        pageSize={pageSize}
      />
    </div>
  );
};

export default StaffTable;
